import { ResponseModelInterfaceStatic } from "@/service/models/response-model-interface";
import { WeeklyDosageVM } from "@/service/view-models/dosage-choices/weekly-dosage-vm";
import { staticImplements } from "@/utilities/static-implements";
import { DayTime } from "@/utilities/types/day-time";
import { Duration } from "@/utilities/types/duration";
import { XmlDocumentWrapper } from "@/utilities/xml-helpers/xml-document-wrapper";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";
import { BaseDosageChoice } from ".";
import { FormulationAndRoute } from "../parts/formulation-and-route";

@staticImplements<ResponseModelInterfaceStatic>()
export class WeeklyDosage extends BaseDosageChoice
{
    public constructor(
        public weekDay: number,
        public time: DayTime,
        public doseValue: number,
        public doseUnit: string,
        public doseInfusionTime: Duration|null,
        public formulationAndRoute: FormulationAndRoute,
    )
    {
        super();
    }

    /**
     * @inheritdoc
     */
    public makeViewModel(): WeeklyDosageVM
    {
        return new WeeklyDosageVM().fillFromModel(this);
    }

    /**
     * @inheritdoc
     */
    public getClosestNextDoseDate(knownDose: Date, current: Date): Date
    {
        if (current.getDay() === this.weekDay && this.time.dateEquals(current)) {
            return current;
        }

        let tmp = new Date(current.getTime());
        while (tmp.getDay() !== this.weekDay) {
            tmp = Duration.fromDays(1).addTo(tmp);
        }

        return this.time.dateAtDay(tmp);
    }

    /**
     * @inheritdoc
     */
    public getClosestPreviousDoseDate(knownDose: Date, current: Date): Date
    {
        if (current.getDay() === this.weekDay && this.time.dateEquals(current)) {
            return current;
        }

        let tmp = new Date(current.getTime());
        while (tmp.getDay() !== this.weekDay) {
            tmp = Duration.fromDays(1).subFrom(tmp);
        }

        return this.time.dateAtDay(tmp);
    }

    /**
     * @inheritdoc
     */
    public getDoseValueForDisplay(): number
    {
        return this.doseValue;
    }

    /**
     * @inheritdoc
     */
    public getDoseUnitForDisplay(): string
    {
        return this.doseUnit;
    }

    /**
     * @inheritdoc
     */
    public getInfusionTimeForDisplay(): Duration|null
    {
        return this.doseInfusionTime;
    }

    /**
     * @inheritdoc
     */
    public getIntervalForDisplay(): Duration
    {
        return Duration.fromDays(7);
    }

    /**
     * @inheritdoc
     */
    public getRouteForDisplay(): FormulationAndRoute
    {
        return this.formulationAndRoute;
    }

    /**
     * @inheritdoc
     */
    public static makeFromXml(elem: XmlElementWrapper): WeeklyDosage
    {
        const doseElem = elem.firstChildByTag("dose");
        const infusionTimeElem = doseElem.firstChildByTagOrNull("infusionTimeInMinutes");

        const infusionTime = (infusionTimeElem === null || infusionTimeElem.valueAsNumber() === 0)
            ? null
            : Duration.fromMinutes(infusionTimeElem.valueAsNumber());

        return new this(
            Math.round(elem.firstChildByTag("day").valueAsNumber()),
            DayTime.makeFromString(elem.firstChildByTag("time").value()),
            doseElem.firstChildByTag("value").valueAsNumber(),
            doseElem.firstChildByTag("unit").value(),
            infusionTime,
            FormulationAndRoute.makeFromXml(doseElem.firstChildByTag("formulationAndRoute")),
        );
    }

    /**
     * @inheritdoc
     */
    public getXml(doc: XmlDocumentWrapper): XmlElementWrapper
    {
        const element = doc.createElement("dailyDosage");

        element.appendNewElement("day", this.weekDay.toFixed(0));
        element.appendNewElement("time", this.time.toString());
        
        const doseElement = element.appendNewElement("dose");
        doseElement.appendNewElement("value", this.doseValue.toString());
        doseElement.appendNewElement("unit", this.doseUnit);

        const infusionTimeInMinutes = (this.doseInfusionTime !== null) ? this.doseInfusionTime.minutes.toFixed(0) : '0';
        doseElement.appendNewElement("infusionTimeInMinutes", infusionTimeInMinutes);

        element.append(this.formulationAndRoute.getXml(doc));

        return element;
    }
}
